import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DEBOUNCE_TIME, URL_PARAMS } from '../constants';

export type UseSearchQueryChangeArgs = {
  pageSize: number;
  cursorBefore: string | undefined;
  cursorAfter: string | undefined;
};

export type UseSearchQueryChangeReturn = {
  /** Current keywords input value (uncontrolled, not debounced) */
  keywordsInputValue: string;
  /** Current supplier ref ID input value (uncontrolled, not debounced) */
  supplierRefIdInputValue: string;
  /** Normalized (trimmed) keywords value */
  keywordsNormalized: string;
  /** Normalized (trimmed) supplier ref ID value */
  supplierRefIdNormalized: string;
  /** Handler for keywords input change */
  onKeywordsInputChange: (value: string) => void;
  /** Handler for supplier ref ID input change */
  onSupplierRefIdInputChange: (value: string) => void;
};

/**
 * Hook for managing search input state and syncing with URL query parameters
 *
 * Features:
 * - Reads initial state from URL query parameters (search, supplierRefId)
 * - Manages uncontrolled input state for search fields
 * - Debounces URL updates (DEBOUNCE_TIME ms) to avoid excessive navigation
 * - Syncs search state and pagination cursors to URL
 * - Provides normalized (trimmed) versions of search values
 *
 * URL Parameters:
 * - search: keywords search term
 * - supplierRefId: supplier reference ID filter
 * - size: page size
 * - after: cursor for forward pagination
 * - before: cursor for backward pagination
 *
 * @param args - Pagination state to sync with URL
 * @returns Search state and handlers
 */
export const useSearchQueryChange = ({
  pageSize,
  cursorBefore,
  cursorAfter,
}: UseSearchQueryChangeArgs): UseSearchQueryChangeReturn => {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  // Parse initial values from URL on mount
  const initialState = useMemo(() => {
    const queryParams = new URLSearchParams(search);
    const initialSearchTerm = (queryParams.get(URL_PARAMS.SEARCH) || '').trim();
    const initialSupplierRefId = (
      queryParams.get(URL_PARAMS.SUPPLIER_REF_ID) || ''
    ).trim();

    return {
      keywords: initialSearchTerm,
      supplierRefId: initialSupplierRefId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reads URL search params once on mount to seed initial input values; subsequent changes are driven by user input and navigate()
  }, []); // Only run on mount

  // Input state (not debounced)
  const [keywordsInputValue, setKeywordsInputValue] = useState(
    initialState.keywords
  );
  const [supplierRefIdInputValue, setSupplierRefIdInputValue] = useState(
    initialState.supplierRefId
  );

  // Normalized values (trimmed)
  const keywordsNormalized = useMemo(
    () => keywordsInputValue.trim(),
    [keywordsInputValue]
  );
  const supplierRefIdNormalized = useMemo(
    () => supplierRefIdInputValue.trim(),
    [supplierRefIdInputValue]
  );

  // Handlers
  const onKeywordsInputChange = useCallback((value: string) => {
    setKeywordsInputValue(value);
  }, []);

  const onSupplierRefIdInputChange = useCallback((value: string) => {
    setSupplierRefIdInputValue(value);
  }, []);

  /**
   * Sync state to URL with debouncing
   *
   * Note: This effect intentionally triggers on every change to the normalized values
   * and pagination state. The debouncing prevents excessive URL updates while still
   * maintaining accurate URL state. This approach ensures the URL is always in sync
   * with the application state, making sharing and bookmarking reliable.
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchParams = new URLSearchParams({
        [URL_PARAMS.PAGE_SIZE]: pageSize.toString(),
        ...(keywordsNormalized && {
          [URL_PARAMS.SEARCH]: keywordsNormalized,
        }),
        ...(supplierRefIdNormalized && {
          [URL_PARAMS.SUPPLIER_REF_ID]: supplierRefIdNormalized,
        }),
        ...(cursorAfter && { [URL_PARAMS.CURSOR_AFTER]: cursorAfter }),
        ...(cursorBefore && { [URL_PARAMS.CURSOR_BEFORE]: cursorBefore }),
      });

      navigate(
        {
          pathname,
          search: searchParams.toString(),
        },
        { replace: true } // Use replace to avoid polluting browser history
      );
    }, DEBOUNCE_TIME);

    return () => clearTimeout(timeoutId);
  }, [
    cursorBefore,
    cursorAfter,
    pageSize,
    keywordsNormalized,
    supplierRefIdNormalized,
    pathname,
    navigate,
  ]);

  return useMemo(
    () => ({
      keywordsInputValue,
      supplierRefIdInputValue,
      keywordsNormalized,
      supplierRefIdNormalized,
      onKeywordsInputChange,
      onSupplierRefIdInputChange,
    }),
    [
      keywordsInputValue,
      supplierRefIdInputValue,
      keywordsNormalized,
      supplierRefIdNormalized,
      onKeywordsInputChange,
      onSupplierRefIdInputChange,
    ]
  );
};
